import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { debounce } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { validatePermission } from "../../../utils/permissionUtils";
import { usePermissions } from "../../../context/permissionContext";
import { DataGrid } from "@mui/x-data-grid";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import useResponsiveColumns from "../../../hooks/useResponsiveColumns";

// Careers reuses the News permission bucket (same content-admin audience) so it
// works without seeding new RBAC entries. Swap to Careers:* if those are added.
const CAREERS_COL_PRIORITY = {
  id: "low",
  updatedAt: "low",
  experience: "medium",
};

const CareersList = () => {
  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("2"); // 2=All, 1=Active, 0=Inactive
  const { permissions, permissionsMapping } = usePermissions();

  const handleQuery = useCallback(
    debounce((e) => setQuery(e.target.value), 500),
    []
  );
  const hasEditPermission = validatePermission(
    "News:Edit",
    permissions,
    permissionsMapping
  );
  const hasDeletePermission = validatePermission(
    "News:Delete",
    permissions,
    permissionsMapping
  );

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("accessToken");
      const response = await axios({
        method: "post",
        url: process.env.REACT_APP_API + "admin/get-listing-jobopeing",
        headers: { "x-access-token": token },
        data: {},
      });
      setLoading(false);
      setAllRows(response?.data?.data?.rows || []);
    } catch (error) {
      setLoading(false);
      Swal.fire({
        title: error?.response?.data?.message || "Failed to load job openings",
        timerProgressBar: true,
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });
    }
  };

  const deleteFunction = (id) => {
    const token = localStorage.getItem("accessToken");
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this job opening!",
      icon: "warning",
      showCancelButton: true,
      showCloseButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios({
          method: "post",
          url: process.env.REACT_APP_API + "admin/delete-jobdetails",
          headers: { "x-access-token": token },
          data: { id },
        })
          .then((response) => {
            setLoading(false);
            if (response?.status === 200) fetchJobs();
          })
          .catch((error) => {
            setLoading(false);
            Swal.fire({
              title: error?.response?.data?.message || "Failed to delete",
              timerProgressBar: true,
              icon: "error",
              confirmButtonText: "OK",
              showCloseButton: true,
            });
          });
      }
    });
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <div className="cellAction">
          <Link
            to={`/careers/${params.row.id}`}
            style={{ textDecoration: "none" }}
          >
            <div className="viewButton">
              <RemoveRedEyeOutlinedIcon />
            </div>
          </Link>
          {hasEditPermission && (
            <Link
              to={`/careers/edit-career/${params.row.id}`}
              style={{ textDecoration: "none" }}
            >
              <div className="viewButton">
                <EditOutlinedIcon />
              </div>
            </Link>
          )}
          {hasDeletePermission && (
            <div
              className="deleteButton"
              onClick={() => deleteFunction(params?.row?.id)}
            >
              <DeleteOutlineIcon />
            </div>
          )}
        </div>
      ),
    },
  ];

  const columnVisibilityModel = useResponsiveColumns(CAREERS_COL_PRIORITY);

  const userColumns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "title", headerName: "Title", flex: 1, minWidth: 160 },
    { field: "location", headerName: "Location", flex: 1, minWidth: 130 },
    { field: "employmentType", headerName: "Type", flex: 1, minWidth: 120 },
    { field: "experience", headerName: "Experience", flex: 1, minWidth: 120 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <div className="cellAction">
          <span
            className={`status ${
              params.row.status == 1 ? "Approved" : "Pending"
            }`}
          >
            {params?.row?.status == 1 ? "Active" : "Inactive"}
          </span>
        </div>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated",
      flex: 1,
      minWidth: 130,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "",
    },
  ];

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      const statusOk = status === "2" || String(r.status) === String(status);
      const queryOk = !q || (r.title || "").toLowerCase().includes(q);
      return statusOk && queryOk;
    });
  }, [allRows, status, query]);

  useEffect(() => {
    fetchJobs();
  }, []);

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Careers" />
      <div className="datatable">
        <div className="datatableTitle">
          <div className="tablesearch">
            <input
              type="text"
              placeholder="Search by title..."
              onChange={handleQuery}
            />
            <SearchOutlinedIcon className="sarchicon" />
          </div>
          <div className="formInput">
            <select
              name="filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value={2}>All</option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
          <Link
            to="/careers/applicants"
            className="link"
            style={{ marginRight: 8 }}
          >
            Applicants
          </Link>
          {hasEditPermission && (
            <Link to="/careers/add-career" className="link">
              Add New
            </Link>
          )}
        </div>
        <DataGrid
          className="datagrid"
          autoHeight
          rows={rows}
          rowsPerPageOptions={[10, 30, 50, 100]}
          pagination
          pageSize={10}
          columns={userColumns.concat(actionColumn)}
          columnVisibilityModel={columnVisibilityModel}
          getRowId={(row) => row.id}
        />
      </div>
    </PageShell>
  );
};

export default CareersList;
